import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { OrderBulkCreateResult, OrderBulkValidateResult } from '../../domain/entities/order-bulk.entity';
import { OrderBulkCreateApiResponse, OrderBulkMapper, OrderBulkValidateApiResponse } from '../mappers/order-bulk.mapper';
import { ApiRepository } from '../../../../shared/infrastructure/api.repository';
import {
  OrderRepositoryPort,
  OrderListPagedRequest,
  OrderListPagedResponse,
  OrderListByPortfolioRequest,
  OrderDocumentListPagedRequest,
  OrderDocumentListPagedResponse,
  OrderDocumentUpload,
  OrderAssetListPagedRequest,
  OrderAssetListPagedResponse,
} from '../../domain/repositories/order.repository.port';
import { Order } from '../../domain/entities/order.entity';
import { OrderStatus } from '../../domain/value-objects/order-status.vo';
import {
  OrderMapper,
  OrderListPagedApiResponse,
  OrderDetailApiResponse,
} from '../mappers/order.mapper';
import { OrderDocumentMapper, OrderDocumentListPagedApiResponse } from '../mappers/order-document.mapper';
import { OrderAssetMapper, OrderAssetGetAllApiResponse } from '../mappers/order-asset.mapper';
import { CreateOrderWithQualityControlDto } from '../../application/dtos/create-order-with-quality-control.dto';
import { EditOrderAssetItemDto, EditOrderAssetsDto } from '../../application/dtos/edit-order-assets.dto';
import { OrderCommunication } from '../../domain/entities/order-communication.entity';
import { OrderCommunicationListApiRespone, OrderCommunicationMapper } from '../mappers/order-communication.mapper';

@Injectable({ providedIn: 'root' })
export class OrderRepository extends ApiRepository implements OrderRepositoryPort {
  private readonly baseUrl = '/orders';

  getListPaged(request: OrderListPagedRequest): Observable<OrderListPagedResponse> {
    return this.post<OrderListPagedApiResponse, OrderListPagedRequest>(
      `${this.baseUrl}/GetListPaged`,
      request,
    ).pipe(
      map((response) => ({
        items: OrderMapper.toDomainList(response.items ?? []),
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalItemsCount: response.totalItemsCount,
        orderField: response.orderField,
        ascendingOrder: response.ascendingOrder,
      }))
    );
  }

  getListPagedByPortfolio(portfolioId: string, request: OrderListByPortfolioRequest): Observable<OrderListPagedResponse> {
    return this.post<OrderListPagedApiResponse, OrderListByPortfolioRequest>(
      `/portfolios/${portfolioId}/OrdersGetListPagedByPortfolio`,
      request,
    ).pipe(
      map((response) => ({
        items: OrderMapper.toDomainList(response.items ?? []),
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalItemsCount: response.totalItemsCount,
        orderField: response.orderField,
        ascendingOrder: response.ascendingOrder,
      }))
    );
  }

  getDetails(id: string): Observable<Order> {
    return this.get<OrderDetailApiResponse>(`${this.baseUrl}/${id}`).pipe(
      map((response) => OrderMapper.toDomain({ ...response}))
    );
  }

  create(
    clientId: string,
    clientReference?: string,
    portfolioId?: string,
    expectedDeliveryDate?: string,
  ): Observable<string> {
    return this.post<string, object>(this.baseUrl, {
      clientId,
      clientReference: clientReference ?? null,
      portfolioId: portfolioId ?? null,
      expectedDeliveryDate: expectedDeliveryDate ?? null,
    });
  }

  createWithQualityControl(dto: CreateOrderWithQualityControlDto): Observable<string> {
    const formData = new FormData();
    formData.append('clientId', dto.clientId);
    if (dto.clientReference) formData.append('clientReference', dto.clientReference);
    if (dto.portfolioId) formData.append('portfolioId', dto.portfolioId);
    if (dto.appraisalCompany != null) formData.append('appraisalCompany', dto.appraisalCompany.toString());
    if (dto.appraisalPurpose != null) formData.append('appraisalPurpose', dto.appraisalPurpose.toString());
    if (dto.appraisalReportNumber) formData.append('appraisalReportNumber', dto.appraisalReportNumber);
    if (dto.appraisalIssueDate) {
      const [day, month, year] = dto.appraisalIssueDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }).split('/');
      formData.append('appraisalIssueDate', new Date(Number(year), Number(month) - 1, Number(day)).toISOString().split('T')[0]);
    }
    if (dto.appraiserName) formData.append('appraiserName', dto.appraiserName);
    if (dto.origin != null) formData.append('origin', dto.origin.toString());

    if (dto.documents?.length) {
      dto.documents.forEach((doc, i) => {
        formData.append(`documents[${i}].formFile`, doc.file);
        formData.append(`documents[${i}].documentType`, doc.documentType.toString());
      });
    }

    if (dto.orderDocuments?.length) {
      dto.orderDocuments.forEach((doc, i) => {
        formData.append(`orderDocuments[${i}].formFile`, doc.file);
        formData.append(`orderDocuments[${i}].documentType`, doc.documentType.toString());
      });
    }

    if (dto.assets?.length) {
      dto.assets.forEach((asset, i) => {
        if (asset.idufir != null) formData.append(`assets[${i}].idufir`, asset.idufir);
        if (asset.propertyRegistryNumber != null) formData.append(`assets[${i}].propertyRegistryNumber`, asset.propertyRegistryNumber);
        if (asset.propertyRegistryEnumValue != null) formData.append(`assets[${i}].propertyRegistryEnumValue`, asset.propertyRegistryEnumValue);
        asset.cadastralReferences?.forEach((ref, j) => {
          formData.append(`assets[${i}].cadastralReferences[${j}]`, ref);
        });
      });
    }
    const req = new HttpRequest('POST', `${this.baseApiUrl}${this.baseUrl}/WithQualityControl`, formData);
    return this.http.request<string>(req).pipe(
      filter((e): e is HttpResponse<string> => e instanceof HttpResponse),
      map(r => r.body as string),
    );
  }

  updateStatus(id: string, newStatus: OrderStatus): Observable<void> {
    const params = new HttpParams().set('newStatus', newStatus.toString());
    return this.http.put<void>(`${this.baseApiUrl}${this.baseUrl}/${id}/status`, null, { params });
  }

  update(id: string, clientId: string, clientReference?: string, portfolioId?: string): Observable<void> {
    return this.put<void, object>(`${this.baseUrl}/${id}`, {
      clientId,
      clientReference: clientReference ?? null,
      portfolioId: portfolioId ?? null,
    });
  }

  getOrderDocuments(request: OrderDocumentListPagedRequest): Observable<OrderDocumentListPagedResponse> {
    return this.post<OrderDocumentListPagedApiResponse, object>(
      `${this.baseUrl}/${request.orderId}/documents/GetListPaged`,
      {
        page: request.page,
        pageSize: request.pageSize,
        orderField: request.orderField,
        ascendingOrder: request.ascendingOrder,
      },
    ).pipe(
      map((response) => ({
        items: OrderDocumentMapper.toDomainList(response.items ?? []),
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalItemsCount: response.totalItemsCount,
      })),
    );
  }

  uploadOrderDocument(orderId: string, file: File, documentType: number): Observable<void> {
    const formData = new FormData();
    formData.append('formFile', file);
    formData.append('documentType', documentType.toString());
    const req = new HttpRequest('POST', `${this.baseApiUrl}${this.baseUrl}/${orderId}/document`, formData);
    return this.http.request<void>(req).pipe(
      filter((e): e is HttpResponse<void> => e instanceof HttpResponse),
      map(() => void 0),
    );
  }

  uploadOrderDocuments(orderId: string, documents: OrderDocumentUpload[]): Observable<void> {
    const formData = new FormData();
    documents.forEach((doc, i) => {
      formData.append(`files[${i}].formFile`, doc.file);
      formData.append(`files[${i}].documentType`, doc.documentType.toString());
    });
    const req = new HttpRequest('POST', `${this.baseApiUrl}${this.baseUrl}/${orderId}/documents`, formData);
    return this.http.request<void>(req).pipe(
      filter((e): e is HttpResponse<void> => e instanceof HttpResponse),
      map(() => void 0),
    );
  }

  deleteOrderDocument(documentId: string): Observable<void> {
    return this.delete<void>(`${this.baseUrl}/documents/${documentId}`);
  }

  getOrderAssets(orderId: string, request: OrderAssetListPagedRequest): Observable<OrderAssetListPagedResponse> {
    return this.post<OrderAssetGetAllApiResponse, object>(
      `${this.baseUrl}/${orderId}/assets/ListPaged`,
      {
        page: request.page,
        pageSize: request.pageSize,
        orderField: request.orderField,
        ascendingOrder: request.ascendingOrder,
        filter: request.filter,
      },
    ).pipe(
      map((response) => ({
        items: OrderAssetMapper.toDomainList(response.items ?? []),
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalItemsCount: response.totalItemsCount,
      })),
    );
  }

  editOrderAssets(orderId: string, dto: EditOrderAssetsDto): Observable<string[]> {
    return this.post<string[], EditOrderAssetsDto>(`${this.baseUrl}/${orderId}/assets`, dto);
  }

  deleteOrderAsset(orderId: string, assetId: string): Observable<void> {
    return this.delete<void>(`${this.baseUrl}/${orderId}/assets/${assetId}`);
  }

  updateOrderAsset(orderId: string, assetId: string, dto: EditOrderAssetItemDto): Observable<void> {
    return this.put<void, EditOrderAssetItemDto>(`${this.baseUrl}/${orderId}/assets/${assetId}`, dto);
  }

  getOrderCommunications(orderId: string): Observable<OrderCommunication[]> {
    return this.get<OrderCommunicationListApiRespone>(`${this.baseUrl}/${orderId}/communications`).pipe(
      map((response) => OrderCommunicationMapper.toDomainList(response.items ?? [])),
        );
  }
  cancelOrder(id: string): Observable<void> {
    return this.http.put<void>(`${this.baseApiUrl}${this.baseUrl}/${id}/cancel`, null);
  }

  downloadBulkTemplate(): Observable<Blob> {
    return this.http.get(`${this.baseApiUrl}${this.baseUrl}/Bulk/Template`, { responseType: 'blob' });
  }

  validateBulkOrders(excelFile: File): Observable<OrderBulkValidateResult> {
    const formData = new FormData();
    formData.append('excelFile', excelFile);
    const req = new HttpRequest('POST', `${this.baseApiUrl}${this.baseUrl}/Bulk/Validate`, formData);
    return this.http.request<OrderBulkValidateApiResponse>(req).pipe(
      filter((e): e is HttpResponse<OrderBulkValidateApiResponse> => e instanceof HttpResponse),
      map(r => OrderBulkMapper.toValidateDomain(r.body!)),
    );
  }

  createBulkOrders(excelFile: File, documentFiles: File[] = []): Observable<OrderBulkCreateResult> {
    const formData = new FormData();
    formData.append('excelFile', excelFile);
    documentFiles.forEach(f => formData.append('documentFiles', f));
    const req = new HttpRequest('POST', `${this.baseApiUrl}${this.baseUrl}/Bulk`, formData);
    return this.http.request<OrderBulkCreateApiResponse>(req).pipe(
      filter((e): e is HttpResponse<OrderBulkCreateApiResponse> => e instanceof HttpResponse),
      map(r => OrderBulkMapper.toCreateDomain(r.body!)),
    );
  }
}

